import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';

import { getLanguageCode } from '../../../lib/context';
import { downloadFromUrl } from '../../../lib/download';
import { formatUtcOffset } from '../../../lib/timezone';
import { getReportExcelUrl, getReportPdfUrl } from '../api/recordsApi';

export type ExportChoice = 'csv' | 'pdf' | 'both';

interface ExportableRecord {
    deviceId: string;
    index: number;
    startTime?: string;
    endTime?: string;
}

type ReportUrlFetcher = (deviceId: string, index: number, timeZone: string, language: string) => Promise<any>;

// Gap between downloads, so the browser doesn't block a burst of them.
const DOWNLOAD_GAP_MS = 300;

const reportFileName = (record: ExportableRecord, extension: string) => {
    const startDate = record.startTime ? dayjs(record.startTime).format('YYYY-MM-DD') : 'unknown';
    const endDate = record.endTime ? dayjs(record.endTime).format('YYYY-MM-DD') : 'unknown';
    return `Data-report-${record.deviceId}-${startDate}-to-${endDate}.${extension}`;
};

// One report per record, one after another. Returns how many failed. A failure is
// logged and skipped, so one bad record doesn't stop the rest of the batch, but it
// is counted: the caller must not report a failed export as a success (#273).
export const downloadReports = async (
    records: ExportableRecord[],
    fetchUrl: ReportUrlFetcher,
    extension: string,
    language: string,
    gapMs = DOWNLOAD_GAP_MS
): Promise<number> => {
    const timeZone = formatUtcOffset();
    let failed = 0;
    for (const record of records) {
        try {
            const response = await fetchUrl(record.deviceId, record.index, timeZone, language);
            if (response?.status !== 'success' || !response.url) {
                throw new Error(response?.message ?? 'no report URL in the response');
            }
            try {
                await downloadFromUrl(response.url, reportFileName(record, extension));
            } catch {
                // Fall back to opening the file; a blocked pop-up means nothing was delivered.
                if (!window.open(response.url, '_blank')) throw new Error('download and pop-up both failed');
            }
        } catch (error) {
            failed += 1;
            console.error(`Error downloading ${extension} report for`, record.deviceId, error);
        }
        await new Promise((resolve) => setTimeout(resolve, gapMs));
    }
    return failed;
};

/**
 * Downloads Excel and/or PDF reports for the selected records. `onAlert` reports
 * progress ("preparing"), then success or failure, through the page's snackbar.
 */
function useRecordExport(onAlert: (severity: string, message: string) => void) {
    const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Records' });
    const [preparing, setPreparing] = useState(false);

    const exportRecords = async (records: ExportableRecord[], choice: ExportChoice) => {
        if (records.length === 0) return;
        const language = getLanguageCode(i18n.language);

        setPreparing(true);
        onAlert('info', t('preparing'));
        try {
            let failed = 0;
            let total = 0;
            if (choice === 'csv' || choice === 'both') {
                failed += await downloadReports(records, getReportExcelUrl, 'xlsx', language);
                total += records.length;
            }
            if (choice === 'pdf' || choice === 'both') {
                failed += await downloadReports(records, getReportPdfUrl, 'pdf', language);
                total += records.length;
            }
            if (failed === 0) onAlert('success', t('exportSuccess'));
            else if (failed === total) onAlert('error', t('exportFailed'));
            else onAlert('warning', t('exportPartial', { failed, total }));
        } catch (error) {
            console.error('Export failed:', error);
            onAlert('error', t('exportFailed'));
        } finally {
            setPreparing(false);
        }
    };

    return { preparing, setPreparing, exportRecords };
}

export default useRecordExport;
